import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Users, Cake, Crown } from "lucide-react";
import { brl } from "@/lib/format";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const LEVEL_TONE = {
  novo: "bg-stone-100 text-stone-700 border-stone-200",
  prata: "bg-slate-100 text-slate-700 border-slate-300",
  ouro: "bg-amber-100 text-amber-800 border-amber-300",
  vip: "bg-orange-100 text-orange-700 border-orange-300",
};

export default function CRM() {
  const [list, setList] = useState([]);
  const [bd, setBd] = useState({ today: [], month: [] });
  const [dlg, setDlg] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", address: "", birthday: "" });

  const load = async () => {
    const [c, b] = await Promise.all([api.get("/customers"), api.get("/customers/birthdays").catch(() => ({ data: { today: [], month: [] } }))]);
    setList(c.data); setBd(b.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/customers", f); toast.success("Cliente cadastrado"); setDlg(false); setF({ name: "", phone: "", email: "", address: "", birthday: "" }); load(); }
    catch { toast.error("Erro"); }
  };

  const CustRow = ({ c }) => (
    <tr key={c.id} className="border-t" data-testid={`customer-${c.id}`}>
      <td className="px-4 py-3 font-medium">
        {c.name}
        {c.level && <Badge variant="outline" className={`ml-2 capitalize ${LEVEL_TONE[c.level.id] || ""}`} data-testid={`level-${c.id}`}>
          <Crown className="mr-1 h-3 w-3" /> {c.level.name}
        </Badge>}
      </td>
      <td className="px-4 py-3 text-stone-500">{c.phone}</td>
      <td className="px-4 py-3 text-stone-500 text-xs">{c.birthday || "—"}</td>
      <td className="px-4 py-3 text-right font-mono-num">{c.orders_count || 0}</td>
      <td className="px-4 py-3 text-right font-mono-num text-emerald-600">{brl(c.cashback_balance || 0)}</td>
      <td className="px-4 py-3 text-right font-mono-num font-bold text-orange-600">{brl(c.total_spent || 0)}</td>
    </tr>
  );

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-display text-3xl font-extrabold text-stone-900">Clientes</h1>
          <p className="text-stone-500">CRM, níveis de fidelidade e aniversariantes.</p></div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild><Button className="gap-2 bg-orange-600 hover:bg-orange-700" data-testid="new-customer-btn"><Plus className="h-4 w-4" /> Novo cliente</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Novo cliente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="c-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Telefone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="c-phone" /></div>
                <div><Label>E-mail</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
              </div>
              <div><Label>Endereço</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
              <div><Label>Aniversário</Label><Input type="date" value={f.birthday} onChange={(e) => setF({ ...f, birthday: e.target.value })} data-testid="c-birthday" /></div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-orange-600 hover:bg-orange-700" data-testid="c-save">Salvar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {bd.today.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/60" data-testid="birthdays-today">
          <CardContent className="p-6 flex items-start gap-3">
            <Cake className="h-6 w-6 text-orange-600 mt-1" />
            <div className="flex-1">
              <div className="font-display font-bold text-stone-900">Aniversariantes de hoje</div>
              <div className="text-sm text-stone-600 mt-1">
                O sistema envia automaticamente um cupom de 15% por e-mail (quando cadastrado).
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {bd.today.map((c) => (
                  <span key={c.id} className="rounded-full bg-white border border-orange-200 px-3 py-1 text-sm">
                    🎂 {c.name}
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="all">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all-cust">Todos ({list.length})</TabsTrigger>
          <TabsTrigger value="birthday" data-testid="tab-birthdays">Aniversariantes ({bd.month.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="all">
          <Card><CardContent className="p-0">
            {list.length === 0 && <div className="p-8 text-center text-stone-500"><Users className="h-8 w-8 mx-auto mb-2" />Cadastre seus primeiros clientes.</div>}
            {list.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-500 text-xs uppercase"><tr>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Telefone</th>
                  <th className="text-left px-4 py-3">Aniversário</th>
                  <th className="text-right px-4 py-3">Pedidos</th>
                  <th className="text-right px-4 py-3">Cashback</th>
                  <th className="text-right px-4 py-3">Total gasto</th>
                </tr></thead>
                <tbody>{list.map((c) => <CustRow key={c.id} c={c} />)}</tbody>
              </table>
            )}
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="birthday">
          <Card><CardContent className="p-0">
            {bd.month.length === 0 && <div className="p-8 text-center text-stone-500"><Cake className="h-8 w-8 mx-auto mb-2" />Nenhum aniversariante neste mês.</div>}
            {bd.month.length > 0 && (
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-500 text-xs uppercase"><tr>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">Telefone</th>
                  <th className="text-left px-4 py-3">Aniversário</th>
                  <th className="text-right px-4 py-3">Pedidos</th>
                  <th className="text-right px-4 py-3">Cashback</th>
                  <th className="text-right px-4 py-3">Total gasto</th>
                </tr></thead>
                <tbody>{bd.month.map((c) => <CustRow key={c.id} c={c} />)}</tbody>
              </table>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
